import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Lightbulb } from "lucide-react";
import { IdeaDetailModal } from "@/components/feed/IdeaDetailModal";
import { format, isToday, parseISO } from "date-fns";
import { useIdeaSocialProof } from "@/hooks/useIdeaSocialProof";
import { HeroSection } from "@/components/ideas/HeroSection";
import { CategoryFilterBar } from "@/components/ideas/CategoryFilterBar";
import { FeaturedArticle } from "@/components/ideas/FeaturedArticle";
import { MostUsefulRail } from "@/components/ideas/MostUsefulRail";
import { FeedItem } from "@/components/ideas/FeedItem";
import { FeedSidebar } from "@/components/ideas/FeedSidebar";
import { IdeaFeedItem } from "@/components/ideas/IdeaFeedItem";
import { getCuratedContentGrouped } from "@/data/curatedContent";
import type { CuratedContentItem } from "@/types/content";

interface Idea {
  title: string;
  description: string;
  initial_goal: string;
  category: string;
  tags: string[];
  difficulty: string;
  why: string;
  problem?: string;
  approach?: string;
  example_output?: string;
  effort_note?: string;
  time_saved?: string;
  who_uses_this?: string;
}

interface DayGroup {
  date: string;
  ideas: Idea[];
}

type FeedbackMap = Record<string, "up" | "down">;

const AIIdeas = () => {
  const navigate = useNavigate();
  const { user, membership } = useAuth();
  const [dayGroups, setDayGroups] = useState<DayGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedbackMap, setFeedbackMap] = useState<FeedbackMap>({});
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const { getSocialProof } = useIdeaSocialProof();

  const curatedGroups = getCuratedContentGrouped();
  const allCuratedItems = curatedGroups.flatMap((g) => g.items);
  const todayCurated = curatedGroups.find((g) => isToday(parseISO(g.date)));
  const pastCuratedItems = curatedGroups
    .filter((g) => !isToday(parseISO(g.date)))
    .flatMap((g) => g.items);

  // Org ideas
  const fetchHistory = useCallback(async () => {
    if (!membership?.org_id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_org_prompt_ideas_history", {
        _org_id: membership.org_id,
      });
      if (error) { console.error("Error fetching ideas history:", error); return; }
      if (data) {
        const today = format(new Date(), "yyyy-MM-dd");
        const groups: DayGroup[] = (data as any[])
          .filter((row) => row.generation_date <= today)
          .map((row) => {
            const parsed = Array.isArray(row.ideas_json) ? row.ideas_json : (row.ideas_json as any)?.ideas || [];
            return { date: row.generation_date, ideas: parsed as Idea[] };
          });
        setDayGroups(groups);
      }
    } catch (err) { console.error("Error:", err); }
    finally { setLoading(false); }
  }, [membership?.org_id]);

  const fetchAllFeedback = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await supabase.from("idea_feedback").select("idea_title, feedback, generation_date").eq("user_id", user.id);
      if (data) {
        const map: FeedbackMap = {};
        data.forEach((row: any) => { map[`${row.generation_date}:${row.idea_title}`] = row.feedback; });
        setFeedbackMap(map);
      }
    } catch (err) { console.error("Error fetching feedback:", err); }
  }, [user]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => { fetchAllFeedback(); }, [fetchAllFeedback]);

  const handleFeedback = async (title: string, category: string, feedback: "up" | "down", generationDate: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!user || !membership?.org_id) return;
    const key = `${generationDate}:${title}`;
    setFeedbackMap((prev) => ({ ...prev, [key]: feedback }));
    try {
      await supabase.from("idea_feedback").upsert(
        { user_id: user.id, org_id: membership.org_id, idea_title: title, idea_category: category, feedback, generation_date: generationDate },
        { onConflict: "user_id,idea_title,generation_date" }
      );
    } catch (err) { console.error("Feedback error:", err); }
  };

  const handleIdeaFeedback = (idea: Idea, fb: "up" | "down", date: string, e?: React.MouseEvent) =>
    handleFeedback(idea.title, idea.category, fb, date, e);

  const handleContentFeedback = (item: CuratedContentItem, fb: "up" | "down") => {
    const date = curatedGroups.find((g) => g.items.some((i) => i.id === item.id))?.date || "";
    handleFeedback(item.title, item.category, fb, date);
  };

  const handleBuildIdea = (idea: Idea) => {
    const parts = [idea.title, idea.description];
    if (idea.problem) parts.push(`Problem: ${idea.problem}`);
    if (idea.approach) parts.push(`Approach: ${idea.approach}`);
    if (idea.example_output) parts.push(`Expected output: ${idea.example_output}`);
    navigate("/prompt", { state: { remixContext: { originalPrompt: parts.join("\n\n") } } });
  };

  const handleBuildContent = (item: CuratedContentItem) => {
    const parts = [item.title, item.description];
    if (item.theStory) parts.push(item.theStory);
    if (item.howTheyDidIt) parts.push(item.howTheyDidIt);
    if (item.theOutput) parts.push(item.theOutput);
    parts.push(`Try This: ${item.tryThis}`);
    navigate("/prompt", { state: { remixContext: { originalPrompt: parts.join("\n\n") } } });
  };

  const navigateToArticle = (item: CuratedContentItem) => {
    navigate(`/ai-ideas/${item.id}`);
  };

  // Filter
  const filterContent = (items: CuratedContentItem[]) =>
    activeCategory ? items.filter((i) => i.category === activeCategory) : items;
  const filterIdeas = (ideas: Idea[]) =>
    activeCategory ? ideas.filter((i) => i.category === activeCategory) : ideas;

  // Featured = first today curated item
  const filteredTodayCurated = filterContent(todayCurated?.items || []);
  const featuredItem = filteredTodayCurated[0] || null;

  // Most useful rail = mix of curated + today's org ideas
  const mostUsefulCurated = filterContent(allCuratedItems).filter((i) => i.id !== featuredItem?.id).slice(0, 4);
    const latestGroup = dayGroups[0];
    const railOrgIdeas = filterIdeas(latestGroup?.ideas || []).slice(0, 4);

  // Feed items = past curated + all org ideas, interleaved
  const filteredPastCurated = filterContent(pastCuratedItems);
    const allOrgIdeas = dayGroups
      .filter((g) => g.date !== latestGroup?.date)
      .flatMap((g) => g.ideas.map((idea) => ({ idea, date: g.date })));
  const filteredOrgIdeas = allOrgIdeas.filter((item) => !activeCategory || item.idea.category === activeCategory);

  // Interleave: 1 curated, then 2 org ideas, repeat
  type FeedEntry = { type: "curated"; item: CuratedContentItem } | { type: "idea"; idea: Idea; date: string };
  const interleavedFeed: FeedEntry[] = [];
  let ci = 0, oi = 0;
  while (ci < filteredPastCurated.length || oi < filteredOrgIdeas.length) {
    if (ci < filteredPastCurated.length) {
      interleavedFeed.push({ type: "curated", item: filteredPastCurated[ci++] });
    }
    for (let k = 0; k < 2 && oi < filteredOrgIdeas.length; k++) {
      const { idea, date } = filteredOrgIdeas[oi++];
      interleavedFeed.push({ type: "idea", idea, date });
    }
  }

  const getContentFeedbackKey = (item: CuratedContentItem) => {
    const date = curatedGroups.find((g) => g.items.some((i) => i.id === item.id))?.date || "";
    return `${date}:${item.title}`;
  };

  return (
    <MainLayout>
      <div
        className="mx-auto"
        style={{ maxWidth: 1100, paddingLeft: 24, paddingRight: 24 }}
      >
        {/* HERO MASTHEAD */}
        <HeroSection />

        {/* CATEGORY RAIL */}
        <CategoryFilterBar activeCategory={activeCategory} onCategoryChange={setActiveCategory} />

        {loading && (
          <div className="space-y-4" style={{ marginBottom: 80 }}>
            <Skeleton className="h-8 w-48" />
            <div className="flex gap-6">
              <Skeleton className="h-[270px] flex-1" />
              <Skeleton className="h-[270px] w-[472px] hidden md:block" />
            </div>
          </div>
        )}

        {!loading && (
          <>
            {/* FEATURED ARTICLE */}
            {featuredItem && (
              <FeaturedArticle
                item={featuredItem}
                onClick={() => navigateToArticle(featuredItem)}
                onBuild={() => handleBuildContent(featuredItem)}
              />
            )}

            {/* IDEAS BASED ON YOUR PROMPTS */}
            <MostUsefulRail
              orgIdeas={railOrgIdeas}
              onIdeaClick={(idea) => { setSelectedIdea(idea); setSelectedDate(latestGroup?.date || ""); }}
            />

            {/* MAIN FEED + SIDEBAR */}
            <div className="flex" style={{ gap: 64, marginBottom: 80 }}>
              {/* Feed column */}
              <div style={{ maxWidth: 720 }} className="flex-1 min-w-0">
                {/* Interleaved feed */}
                {interleavedFeed.map((entry, i) => {
                  if (entry.type === "curated") {
                    return (
                      <FeedItem
                        key={`c-${entry.item.id}`}
                        item={entry.item}
                        feedback={feedbackMap[getContentFeedbackKey(entry.item)]}
                        onClick={() => navigateToArticle(entry.item)}
                        onFeedback={handleContentFeedback}
                        onBuild={() => handleBuildContent(entry.item)}
                      />
                    );
                  }
                  const feedbackKey = `${entry.date}:${entry.idea.title}`;
                  return (
                    <IdeaFeedItem
                      key={`i-${entry.date}-${i}`}
                      idea={entry.idea}
                      feedback={feedbackMap[feedbackKey]}
                      socialProof={getSocialProof(entry.idea.category)}
                      onClick={() => { setSelectedIdea(entry.idea); setSelectedDate(entry.date); }}
                      onFeedback={(fb, e) => handleIdeaFeedback(entry.idea, fb, entry.date, e)}
                      onBuild={(e) => { e.stopPropagation(); handleBuildIdea(entry.idea); }}
                    />
                  );
                })}

                {/* Empty state */}
                {interleavedFeed.length === 0 && !featuredItem && mostUsefulCurated.length === 0 && railOrgIdeas.length === 0 && (
                  <div className="text-center" style={{ padding: "80px 0" }}>
                    <Lightbulb className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
                    <p className="text-foreground font-medium" style={{ fontSize: 18 }}>No ideas yet</p>
                    <p className="text-muted-foreground mt-1" style={{ fontSize: 14 }}>
                      Ideas will appear here as they are generated for your team.
                    </p>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <FeedSidebar
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
              />
            </div>

            {/* FOOTER */}
            <div
              className="text-center text-muted-foreground border-t border-border"
              style={{ marginTop: 120, padding: "40px 0", fontSize: 13 }}
            >
              Curated daily for your team · Powered by Cosmo AI
            </div>
          </>
        )}
      </div>

      {/* Modals - only org ideas use modal now */}
      <IdeaDetailModal
        isOpen={!!selectedIdea}
        onClose={() => { setSelectedIdea(null); setSelectedDate(""); }}
        idea={selectedIdea}
        feedback={selectedIdea && selectedDate ? feedbackMap[`${selectedDate}:${selectedIdea.title}`] : undefined}
        onFeedback={(idea, fb) => handleIdeaFeedback(idea, fb, selectedDate)}
        onBuild={handleBuildIdea}
      />
    </MainLayout>
  );
};

export default AIIdeas;
